import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, FileSpreadsheet, ArrowRight, Check, X, AlertTriangle,
  Users, ChevronRight, ChevronLeft, Building2, Sun
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { csvAPI } from '../lib/api';
import { toast } from 'sonner';

// All available fields for mapping - organized by category
const FIELD_GROUPS = {
  customer: {
    label: 'Klant / Customer',
    fields: [
      { key: 'first_name', label: 'Voornaam / First Name', required: true },
      { key: 'last_name', label: 'Naam / Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Telefoonnummer / Phone' },
      { key: 'company_name', label: 'Bedrijf / Company' },
      { key: 'language', label: 'Taal / Language' },
      { key: 'vat_number', label: 'BTW / VAT Number' },
      { key: 'sales_person', label: 'Verkoper / Sales Person' },
    ]
  },
  property: {
    label: 'Adres / Property',
    fields: [
      { key: 'street', label: 'Adres / Street' },
      { key: 'number', label: 'Huisnummer / Number' },
      { key: 'postal_code', label: 'Postcode' },
      { key: 'city', label: 'Gemeente / City' },
    ]
  },
  installation: {
    label: 'Installatie / Installation',
    fields: [
      { key: 'installation_date', label: 'Datum plaatsing zonnepanelen' },
      { key: 'number_of_panels', label: 'Aantal zonnepanelen' },
      { key: 'panel_wattage', label: 'Wp zonnepanelen' },
      { key: 'panel_brand', label: 'Merk zonnepanelen' },
      { key: 'grid_voltage', label: 'Netspanning' },
      { key: 'inverter_brand', label: 'Omvormer' },
      { key: 'inverter_installation_date', label: 'Datum plaatsing omvormer' },
      { key: 'battery', label: 'Batterij' },
      { key: 'ev_charger', label: 'Laadpaal' },
      { key: 'inspection_date', label: 'Datum Keuring' },
      { key: 'inspection_file_number', label: 'Dossiernummer Keuring' },
    ]
  },
  other: {
    label: 'Overige / Other',
    fields: [
      { key: 'notes', label: 'Notities / Notes' },
    ]
  }
};

const ALL_FIELDS = Object.values(FIELD_GROUPS).flatMap(g => g.fields);
const REQUIRED_FIELDS = ['first_name'];  // Only first_name is truly required

// Auto-mapping dictionary (Dutch -> field key)
const AUTO_MAP_DICT = {
  'voornaam': 'first_name',
  'firstname': 'first_name',
  'first name': 'first_name',
  'naam': 'last_name',
  'achternaam': 'last_name',
  'lastname': 'last_name',
  'last name': 'last_name',
  'email': 'email',
  'e-mail': 'email',
  'mail': 'email',
  'telefoon': 'phone',
  'telefoonnummer': 'phone',
  'gsm': 'phone',
  'phone': 'phone',
  'bedrijf': 'company_name',
  'company': 'company_name',
  'firma': 'company_name',
  'taal': 'language',
  'language': 'language',
  'btw': 'vat_number',
  'btw nummer': 'vat_number',
  'vat': 'vat_number',
  'verkoper': 'sales_person',
  'verkoper van het project': 'sales_person',
  'sales': 'sales_person',
  'adres': 'street',
  'straat': 'street',
  'street': 'street',
  'address': 'street',
  'huisnummer': 'number',
  'nummer': 'number',
  'number': 'number',
  'postcode': 'postal_code',
  'zip': 'postal_code',
  'postalcode': 'postal_code',
  'gemeente': 'city',
  'stad': 'city',
  'city': 'city',
  'plaats': 'city',
  'datum plaatsing zonnepanelen': 'installation_date',
  'installatie datum': 'installation_date',
  'installation date': 'installation_date',
  'aantal zonnepanelen': 'number_of_panels',
  'aantal panelen': 'number_of_panels',
  'panelen': 'number_of_panels',
  'wp zonnepanelen': 'panel_wattage',
  'wp': 'panel_wattage',
  'wattage': 'panel_wattage',
  'vermogen': 'panel_wattage',
  'merk zonnepanelen': 'panel_brand',
  'merk panelen': 'panel_brand',
  'panel brand': 'panel_brand',
  'netspanning': 'grid_voltage',
  'spanning': 'grid_voltage',
  'voltage': 'grid_voltage',
  'omvormer': 'inverter_brand',
  'inverter': 'inverter_brand',
  'datum plaatsing omvormer': 'inverter_installation_date',
  'batterij': 'battery',
  'battery': 'battery',
  'accu': 'battery',
  'laadpaal': 'ev_charger',
  'charger': 'ev_charger',
  'ev charger': 'ev_charger',
  'oplaadpunt': 'ev_charger',
  'datum keuring': 'inspection_date',
  'keuring datum': 'inspection_date',
  'keuringsdatum': 'inspection_date',
  'dossiernummer keuring': 'inspection_file_number',
  'dossiernummer': 'inspection_file_number',
  'keuring nummer': 'inspection_file_number',
  'notities': 'notes',
  'notes': 'notes',
  'opmerkingen': 'notes',
};

const CSVImport = () => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Selecteer een CSV bestand');
      return;
    }

    setFile(selectedFile);
    toast.info('CSV wordt ingelezen...');
    
    try {
      const response = await csvAPI.preview(selectedFile);
      setPreview(response.data);
      
      toast.success(`${response.data.row_count} rijen gevonden`);
      
      // Auto-map columns based on header names
      const autoMapping = {};
      response.data.headers.forEach(header => {
        const normalizedHeader = header.toLowerCase().trim();
        
        // Check exact match first
        if (AUTO_MAP_DICT[normalizedHeader]) {
          autoMapping[AUTO_MAP_DICT[normalizedHeader]] = header;
          return;
        }
        
        // Check partial matches
        for (const [key, fieldKey] of Object.entries(AUTO_MAP_DICT)) {
          if (normalizedHeader.includes(key) || key.includes(normalizedHeader)) {
            if (!autoMapping[fieldKey]) {
              autoMapping[fieldKey] = header;
            }
            break;
          }
        }
      });
      
      setMapping(autoMapping);
      setStep(2);
    } catch (error) {
      console.error('CSV read error:', error);
      toast.error('Kon CSV bestand niet lezen. Controleer het formaat.');
    }
  };

  const handleMappingChange = (field, csvColumn) => {
    setMapping(prev => ({
      ...prev,
      [field]: csvColumn === 'none' ? undefined : csvColumn
    }));
  };

  const isMappingComplete = () => {
    return REQUIRED_FIELDS.every(field => mapping[field]);
  };

  const getMappedCount = () => {
    return Object.values(mapping).filter(v => v).length;
  };

  const handleImport = async () => {
    if (!isMappingComplete()) {
      toast.error('Map alle verplichte velden');
      return;
    }

    setImporting(true);
    toast.info(`Bezig met importeren van ${preview.row_count} rijen...`);
    
    try {
      const response = await csvAPI.import({
        rows: preview.rows,  // Send ALL rows
        mapping: mapping
      });
      setResult(response.data);
      setStep(4);
      
      if (response.data.errors && response.data.errors.length > 0) {
        console.log('Import errors:', response.data.errors);
      }
      
      toast.success(`Import voltooid! ${response.data.imported_customers} klanten geïmporteerd`);
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Import mislukt: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setMapping({});
    setResult(null);
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl mx-auto" data-testid="csv-import-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight">CSV Import</h1>
        <p className="text-muted-foreground mt-1">Importeer klanten, adressen en installaties vanuit een CSV bestand</p>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-4 mb-8">
        {[
          { num: 1, label: 'Upload' },
          { num: 2, label: 'Velden Koppelen' },
          { num: 3, label: 'Voorbeeld' },
          { num: 4, label: 'Voltooid' }
        ].map((s, i) => (
          <React.Fragment key={s.num}>
            <div className={`flex items-center gap-2 ${step >= s.num ? 'text-primary' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step > s.num ? 'bg-primary text-primary-foreground' :
                step === s.num ? 'bg-primary/20 border-2 border-primary' : 'bg-muted'
              }`}>
                {step > s.num ? <Check className="w-4 h-4" /> : s.num}
              </div>
              <span className="text-sm font-medium hidden sm:inline">{s.label}</span>
            </div>
            {i < 3 && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <Card className="border-dashed border-2 hover:border-primary/50 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <CardContent className="p-12 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="csv-file-input"
            />
            <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium mb-2">Sleep je CSV bestand hier</p>
            <p className="text-sm text-muted-foreground mb-4">of klik om te bladeren</p>
            <Button variant="outline">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Selecteer CSV Bestand
            </Button>
            
            <div className="mt-8 text-left max-w-md mx-auto">
              <p className="text-sm font-medium mb-2">Ondersteunde velden:</p>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>• Klant: Voornaam, Naam, Email, Telefoon, Bedrijf, Taal, BTW, Verkoper</p>
                <p>• Adres: Straat, Nummer, Postcode, Gemeente</p>
                <p>• Installatie: Datum, Aantal panelen, Wp, Merk, Omvormer, Batterij, Laadpaal, Keuring</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Map Fields */}
      {step === 2 && preview && (
        <Card>
          <CardHeader>
            <CardTitle>Koppel CSV Kolommen</CardTitle>
            <CardDescription>
              Koppel je CSV kolommen aan de juiste velden. Verplichte velden zijn gemarkeerd met *
              <br />
              <span className="text-primary">{getMappedCount()} van {ALL_FIELDS.length} velden gekoppeld</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {Object.entries(FIELD_GROUPS).map(([groupKey, group]) => (
              <div key={groupKey}>
                <div className="flex items-center gap-2 mb-3">
                  {groupKey === 'customer' && <Users className="w-4 h-4 text-primary" />}
                  {groupKey === 'property' && <Building2 className="w-4 h-4 text-blue-400" />}
                  {groupKey === 'installation' && <Sun className="w-4 h-4 text-amber-400" />}
                  <h3 className="font-medium">{group.label}</h3>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {group.fields.map(field => (
                    <div key={field.key} className="flex items-center gap-3">
                      <div className="w-44 flex-shrink-0">
                        <span className="text-sm">
                          {field.label}
                          {field.required && <span className="text-destructive ml-1">*</span>}
                        </span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <Select 
                        value={mapping[field.key] || 'none'} 
                        onValueChange={(value) => handleMappingChange(field.key, value)}
                      >
                        <SelectTrigger className="flex-1" data-testid={`mapping-${field.key}`}>
                          <SelectValue placeholder="Selecteer kolom" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Niet gekoppeld --</SelectItem>
                          {preview.headers.filter(header => header && header.trim() !== '').map(header => (
                            <SelectItem key={header} value={header}>{header}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {mapping[field.key] && (
                        <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30 flex-shrink-0">
                          <Check className="w-3 h-3" />
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
                <Separator className="mt-4" />
              </div>
            ))}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={resetImport}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Terug
              </Button>
              <Button onClick={() => setStep(3)} disabled={!isMappingComplete()} data-testid="continue-to-preview">
                Doorgaan
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Preview */}
      {step === 3 && preview && (
        <Card>
          <CardHeader>
            <CardTitle>Voorbeeld Import</CardTitle>
            <CardDescription>
              Controleer de data voordat je importeert. Eerste {Math.min(preview.rows.length, 10)} van {preview.row_count} rijen worden getoond.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Voornaam</TableHead>
                    <TableHead>Naam</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Adres</TableHead>
                    <TableHead>Panelen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.slice(0, 10).map((row, i) => (
                    <TableRow key={i}>
                      <TableCell>{row[mapping.first_name] || '-'}</TableCell>
                      <TableCell>{row[mapping.last_name] || '-'}</TableCell>
                      <TableCell>{row[mapping.email] || '-'}</TableCell>
                      <TableCell>
                        {mapping.street && row[mapping.street] ? (
                          `${row[mapping.street]} ${row[mapping.number] || ''}, ${row[mapping.city] || ''}`
                        ) : '-'}
                      </TableCell>
                      <TableCell>{row[mapping.number_of_panels] || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 p-4 rounded-md bg-blue-500/10 border border-blue-500/30">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-blue-400 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-400">Slimme Import</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    De import maakt automatisch klanten, adressen én installaties aan. 
                    Bestaande klanten (zelfde email) worden niet opnieuw aangemaakt.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-between pt-6 border-t mt-6">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ChevronLeft className="w-4 h-4 mr-2" />
                Terug
              </Button>
              <Button onClick={handleImport} disabled={importing} data-testid="import-btn">
                {importing ? 'Bezig met importeren...' : `Importeer ${preview.row_count} Rijen`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Complete */}
      {step === 4 && result && (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Import Voltooid!</h2>
            
            <div className="grid grid-cols-3 gap-4 max-w-md mx-auto my-6">
              <div className="p-4 rounded-md bg-primary/10">
                <p className="text-2xl font-bold text-primary">{result.imported_customers}</p>
                <p className="text-xs text-muted-foreground">Klanten</p>
              </div>
              <div className="p-4 rounded-md bg-blue-500/10">
                <p className="text-2xl font-bold text-blue-400">{result.imported_properties}</p>
                <p className="text-xs text-muted-foreground">Adressen</p>
              </div>
              <div className="p-4 rounded-md bg-amber-500/10">
                <p className="text-2xl font-bold text-amber-400">{result.imported_installations}</p>
                <p className="text-xs text-muted-foreground">Installaties</p>
              </div>
            </div>

            {result.skipped > 0 && (
              <p className="text-sm text-muted-foreground mb-6">
                {result.skipped} rijen overgeslagen (ontbrekende verplichte velden of duplicaten)
              </p>
            )}

            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={resetImport}>
                Meer Importeren
              </Button>
              <Button onClick={() => navigate('/customers')}>
                <Users className="w-4 h-4 mr-2" />
                Bekijk Klanten
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CSVImport;
